import { AlertCircle, Check, FolderSync } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Button as NewButton } from '@/components/ui/NewButton'
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures'
import type { FolderTree as FolderTreeType } from '@/hooks/useFolders'
import { useFolderTree } from '@/hooks/useFolders'
import { useOAuthPopupFlow } from '@/hooks/useOAuthPopupFlow'
import { backendClient } from '@/lib/api-client'
import { MICROSOFT_FILES_OAUTH_PROVIDER_KEY } from '@/lib/microsoftFiles'
import { cn } from '@/lib/utils'
import { isInCorrespondenceFolderTree } from '@/utils/document-utils'
import { FolderTree } from '../folders/FolderTree'
import { Loading } from '../shared'
import { AddSyncV2Wizard } from './AddSyncV2Wizard'
import { MicrosoftFilesPicker } from './MicrosoftFilesPicker'
import { SyncProviderCard } from './SyncProviderCard'
import { AVAILABLE_PROVIDERS, getAvailableSyncProviders, type SyncProviderType } from './syncProviders'
import { type FolderItem, WorkspacePicker } from './WorkspacePicker'

interface SyncFolder {
  id: number
  provider_type: string
  destination_folder_id: number | null
  destination_folder_guid: string | null
}

interface AddSyncDialogProps {
  matterGuid: string
  syncFolders: SyncFolder[]
  onSyncAdded?: (syncFolderId: number) => void
  trigger?: React.ReactNode
  initialProvider?: SyncProviderType
}

export function AddSyncDialog({ matterGuid, syncFolders, onSyncAdded, trigger, initialProvider }: AddSyncDialogProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<'provider' | 'auth' | 'folder' | 'destination'>('provider')
  const [v2Flow, setV2Flow] = useState(false)
  const interceptDismissRef = useRef<() => boolean>(() => false)

  const { isEnabled: iManageEnabled } = useFeatures(FEATURE_FLAGS.IMANAGE)
  const { isEnabled: oneDriveEnabled } = useFeatures(FEATURE_FLAGS.ONEDRIVE)
  const { isEnabled: sharePointEnabled } = useFeatures(FEATURE_FLAGS.SHAREPOINT)

  // Provider selection
  const [selectedProvider, setSelectedProvider] = useState<string>('')

  // Authentication
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [providerConfig, setProviderConfig] = useState<Record<string, any>>({})

  // Folder selection (external source folder)
  const [selectedFolder, setSelectedFolder] = useState<FolderItem | null>(null)
  const [selectedFolders, setSelectedFolders] = useState<FolderItem[]>([]) // For multi-select (Outlook)

  // Destination folder selection (Crimson folder where synced docs will go)
  // undefined = no selection made yet, null = root folder selected, FolderTreeType = specific folder selected
  const [destinationFolder, setDestinationFolder] = useState<FolderTreeType | null | undefined>(undefined)

  // Use folder tree hook for destination selection
  const { folderTree, isLoading: loadingFolders } = useFolderTree(matterGuid)

  // Track active message handlers for cleanup
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null)

  const availableProviders = getAvailableSyncProviders({
    iManageEnabled,
    oneDriveEnabled,
    sharePointEnabled
  })

  // Cleanup message handler when dialog closes or component unmounts
  useEffect(() => {
    if (!isOpen && messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current)
      messageHandlerRef.current = null
    }

    // Cleanup on unmount
    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current)
        messageHandlerRef.current = null
      }
    }
  }, [isOpen])

  // Get folder IDs that are synced by Outlook (for restrictions)
  const outlookSyncedFolderIds = new Set<number>()
  const outlookRootSyncFolderIds = new Set<number>()

  syncFolders.forEach(sync => {
    if (sync.provider_type === 'outlook' && sync.destination_folder_id) {
      outlookRootSyncFolderIds.add(sync.destination_folder_id)
    }
  })

  // Helper to collect all folder IDs that have Outlook syncs or are descendants of Outlook sync roots
  const collectOutlookSyncedFolders = (folder: FolderTreeType, underOutlookSync: boolean = false) => {
    const isOutlookRoot = outlookRootSyncFolderIds.has(folder.folder_id)
    const isUnderSync = underOutlookSync || isOutlookRoot

    if (isUnderSync) {
      outlookSyncedFolderIds.add(folder.folder_id)
    }

    if (folder.children && folder.children.length > 0) {
      folder.children.forEach(child => {
        collectOutlookSyncedFolders(child, isUnderSync)
      })
    }
  }

  // Build the set of Outlook synced folders
  if (folderTree && folderTree.length > 0) {
    folderTree.forEach(rootFolder => {
      collectOutlookSyncedFolders(rootFolder)
    })
  }

  // Helper to check if a folder itself has an Outlook sync (not its descendants)
  const folderHasOutlookSync = (folder: FolderTreeType): boolean => {
    return outlookSyncedFolderIds.has(folder.folder_id)
  }

  // Sync creation
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMicrosoftFilesProvider = selectedProvider === 'onedrive' || selectedProvider === 'sharepoint'
  const { runOAuthPopup } = useOAuthPopupFlow()

  const handleProviderSelect = async (providerType: SyncProviderType) => {
    setSelectedProvider(providerType)
    setError(null)

    // Special handling for Outlook - we already have session token from Azure AD login
    // No need for separate OAuth flow since Outlook uses the same authentication
    if (providerType === 'outlook') {
      setAccessToken('USE_SESSION_TOKEN') // Magic value that backend will recognize
      setProviderConfig({}) // Will be populated from session
      setStep('folder')
      return
    }

    if (providerType === 'imanage') {
      setV2Flow(true)
      return
    }

    if (providerType === 'onedrive' || providerType === 'sharepoint') {
      setIsAuthenticating(true)
      setStep('auth')
      setTimeout(() => {
        handleAuthenticateForProvider(providerType)
      }, 100)
      return
    }

    // Other providers use the generic auth flow.
    // N.B. Do we have any providers that don't require auth?
    // TODO: Check if we have any providers that don't require auth and remove this check.
    setStep('auth')
  }

  const handleAuthenticateForProvider = async (providerType: string) => {
    setIsAuthenticating(true)
    setError(null)

    try {
      // For iManage, use the existing OAuth flow
      if (providerType === 'imanage') {
        const response = await backendClient.get('/imanage/oauth/authorize')
        const data = response.data

        if (data.auth_url) {
          if (!data.state) {
            setError('Invalid iManage authorization response. Please try again.')
            return
          }

          const authData = await runOAuthPopup({
            authUrl: data.auth_url,
            popupName: 'iManageAuth',
            successMessageType: 'imanage-auth-success',
            acknowledgmentMessageType: 'imanage-auth-acknowledged',
            stateStorage: {
              key: 'imanage_oauth_state',
              value: data.state
            },
            extractAuthData: (eventData) => eventData?.authData || null
          })

          const imanageAccountId = authData.imanage_account_id
          if (!imanageAccountId) {
            setError('iManage authentication payload is missing account ID. Please reconnect iManage.')
            return
          }

          setAccessToken(authData.access_token)
          setProviderConfig({
            access_token: authData.access_token,
            dialog_token: authData.dialog_token,
            user_id: authData.user_id,
            customer_id: authData.customer_id,
            imanage_account_id: imanageAccountId
          })
          setStep('folder')
        }
      } else if (providerType === 'onedrive' || providerType === 'sharepoint') {
        const response = await backendClient.get('/onedrive/oauth/authorize', {
          params: { source_provider: providerType }
        })
        const data = response.data

        if (data.auth_url) {
          const authUrl = new URL(data.auth_url)
          const oauthState = authUrl.searchParams.get('state')
          if (!oauthState) {
            setError('Invalid OneDrive authorization URL. Please try again.')
            return
          }

          sessionStorage.setItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY, providerType)
          const authData = await runOAuthPopup({
            authUrl: data.auth_url,
            popupName: 'OneDriveAuth',
            successMessageType: 'onedrive-auth-success',
            acknowledgmentMessageType: 'onedrive-auth-acknowledged',
            stateStorage: {
              key: 'onedrive_oauth_state',
              value: oauthState
            },
            extractAuthData: (eventData) => {
              if (!eventData?.authData?.access_token || !eventData?.authData?.oauth_account_id) {
                return null
              }
              return eventData.authData
            }
          }).finally(() => {
            sessionStorage.removeItem(MICROSOFT_FILES_OAUTH_PROVIDER_KEY)
          })

          setAccessToken(authData.access_token)
          setProviderConfig({
            access_token: authData.access_token,
            oauth_account_id: authData.oauth_account_id
          })
          setStep('folder')
        }
      } else {
        // For other providers, use generic sync auth endpoint
        const response = await backendClient.post('/sync/auth/initiate', {
          provider_type: selectedProvider,
          matter_guid: matterGuid
        })

        const data = response.data

        if (data.auth_url) {
          const authWindow = window.open(
            data.auth_url,
            'oauth',
            'width=600,height=700,scrollbars=yes'
          )

          const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'oauth-success') {
              setAccessToken(event.data.access_token)
              setProviderConfig(event.data.provider_config || {})
              setStep('folder')
              authWindow?.close()
              window.removeEventListener('message', handleMessage)
              messageHandlerRef.current = null
            } else if (event.data.type === 'oauth-error') {
              setError(event.data.error || 'Authentication failed')
              authWindow?.close()
              window.removeEventListener('message', handleMessage)
              messageHandlerRef.current = null
            }
          }

          // Remove any existing handler before adding new one
          if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current)
          }
          messageHandlerRef.current = handleMessage
          window.addEventListener('message', handleMessage)
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Authentication failed'
      setError(errorMessage)
    } finally {
      setIsAuthenticating(false)
    }
  }

  const handleOutlookFolderSelected = (folder: FolderItem | null, allFolders?: FolderItem[]) => {
    // Multi-select mode - allFolders will always be provided
    if (allFolders !== undefined) {
      setSelectedFolders(allFolders)
      setSelectedFolder(allFolders.length > 0 ? allFolders[0] : null) // Keep first for display or null if empty
      return
    }

    // Fallback to single folder (for non-Outlook or single-select)
    if (folder) {
      setSelectedFolder(folder)
      setSelectedFolders([folder])
      // OneDrive/SharePoint: stay on folder step so selection is visible; user clicks Next (browse-only flow)
      if (!isMicrosoftFilesProvider) {
        setStep('destination')
      }
    } else {
      setSelectedFolder(null)
      setSelectedFolders([])
    }
  }

  const handleFolderNext = () => {
    // For Outlook multi-select, check selectedFolders
    // For others, check selectedFolder
    if (selectedProvider === 'outlook' && selectedFolders.length === 0) {
      setError('Please select at least one folder')
      return
    }

    if (selectedProvider !== 'outlook' && !selectedFolder) {
      setError('Please select a folder')
      return
    }

    // Move to destination selection (folders already loaded via hook)
    setStep('destination')
  }

  const handleCreateSync = async () => {
    const foldersToSync = selectedFolders.length > 0 ? selectedFolders : (selectedFolder ? [selectedFolder] : [])

    if (foldersToSync.length === 0) {
      setError('Please select at least one source folder')
      return
    }

    // destinationFolder can be null (for root) or a folder object
    // We check for undefined to ensure user made a selection
    if (destinationFolder === undefined) {
      setError('Please select a destination folder')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const createdSyncIds: number[] = []

      // For Outlook multi-select, create a sync for each selected folder
      for (const folder of foldersToSync) {
        const od = isMicrosoftFilesProvider
        const providerTypeForCreate = od ? 'onedrive' : selectedProvider
        const response = await backendClient.post(`/sync/folders?matter_guid=${matterGuid}`, {
          provider_type: providerTypeForCreate,
          destination_folder_id: destinationFolder?.folder_id || null,
          provider_config: od
            ? {
                ...providerConfig,
                drive_id: folder.drive_id,
                onedrive_item_id: folder.id,
                folder_name: folder.name,
                folder_path: folder.path,
                is_drive_root: folder.is_drive_root,
                drive_name: folder.drive_name,
                oauth_account_id: providerConfig.oauth_account_id,
                source_provider: selectedProvider
              }
            : {
                ...providerConfig,
                folder_id: folder.id,
                folder_name: folder.name,
                folder_path: folder.path,
                workspace_id: (folder as any).workspace_id,
                workspace_name: (folder as any).workspace_name,
              }
        })

        const data = response.data
        createdSyncIds.push(data.sync_folder_id)

        // For iManage syncs, immediately trigger the first sync for instant feedback
        if (isMicrosoftFilesProvider) {
          try {
            await backendClient.post(`/sync/folders/${data.sync_folder_id}/run?matter_guid=${matterGuid}`)
          } catch (syncErr) {
            console.warn('Failed to trigger immediate sync:', syncErr)
          }
        }
      }

      // Success!
      setIsOpen(false)

      // Call onSyncAdded for each created sync (or just the first one for backward compat)
      if (createdSyncIds.length > 0) {
        onSyncAdded?.(createdSyncIds[0])
      }

      // Reset state
      resetDialog()
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create sync'
      setError(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  const resetDialog = () => {
    setStep('provider')
    setSelectedProvider('')
    setAccessToken(null)
    setProviderConfig({})
    setSelectedFolder(null)
    setSelectedFolders([])
    setDestinationFolder(undefined)
    setError(null)
    setV2Flow(false)
  }

  // Handle dialog open/close state changes
  const handleOpenChange = (open: boolean) => {
    if (!open && interceptDismissRef.current()) {
      return
    }
    setIsOpen(open)
    if (!open) {
      resetDialog()
      return
    }
    resetDialog()
    if (initialProvider) {
      void handleProviderSelect(initialProvider)
    }
  }

  const selectedProviderInfo = AVAILABLE_PROVIDERS.find(p => p.type === selectedProvider)
  const isBrowseStep = step === 'folder' || step === 'destination'

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <NewButton icon={FolderSync} text='Add Folder Sync' />
        )}
      </DialogTrigger>

      {v2Flow ? (
        <AddSyncV2Wizard
          matterGuid={matterGuid}
          onCreated={(syncFolderId) => {
            onSyncAdded?.(syncFolderId)
            setIsOpen(false)
            resetDialog()
          }}
          onCancel={() => {
            setIsOpen(false)
            resetDialog()
          }}
          interceptDismissRef={interceptDismissRef}
        />
      ) : (
      <>
      <DialogContent
        className={cn(
          'flex flex-col w-[80vw] max-w-5xl',
          isBrowseStep
            ? 'h-[80vh] max-h-[85vh] overflow-hidden'
            : 'max-h-[80vh]'
        )}
      >
        <DialogHeader>
          <DialogTitle>Add External Folder Sync</DialogTitle>
          <DialogDescription>
            Sync documents from an external folder to this matter
          </DialogDescription>
        </DialogHeader>

        <div className={cn('flex-1', isBrowseStep ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-y-auto')}>
          {/* Progress Summary - Shows what's been selected */}
          {step !== 'provider' && (
            <div className="mb-4 shrink-0 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex items-center gap-3 text-sm">
                {selectedProviderInfo && (
                  <>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="font-medium">
                        {selectedFolders.length > 1
                          ? `${selectedFolders.length} folders (${selectedProviderInfo.name})`
                          : selectedFolder
                          ? `${selectedFolder.name} (${selectedProviderInfo.name})`
                          : selectedProviderInfo.name
                        }
                      </span>
                    </div>
                    {destinationFolder !== undefined && (
                      <>
                        <span className="text-gray-400">→</span>
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-600" />
                          <span className="font-medium">
                            {destinationFolder ? destinationFolder.name : 'Root'} (Crimson)
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 shrink-0 text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Provider Selection */}
          {step === 'provider' && (
            <div className="pt-2 lg:pt-4 grid gap-3">
              {availableProviders.map((provider) => (
                <SyncProviderCard
                  key={provider.type}
                  provider={provider}
                  selected={selectedProvider === provider.type}
                  onClick={() => handleProviderSelect(provider.type)}
                />
              ))}
            </div>
          )}

          {/* Step 2: Authentication */}
          {step === 'auth' && (
            <div className="space-y-4">
              <div className="text-center py-8">
                <div className="mb-4">
                  <div className="text-lg font-semibold">Connect to {selectedProviderInfo?.name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    You'll be redirected to authenticate with {selectedProviderInfo?.name}
                  </div>
                </div>

                <NewButton
                  onClick={() => handleAuthenticateForProvider(selectedProvider)}
                  loading={isAuthenticating}
                  loadingText='Connecting'
                  text={`Connect to ${selectedProviderInfo?.name}`}
                  size="lg"
                />
              </div>
            </div>
          )}

          {/* Keep MicrosoftFilesPicker mounted on destination so Back does not clear the chosen folder. */}
          {accessToken && (step === 'folder' || (step === 'destination' && isMicrosoftFilesProvider)) && (
            <div className={cn('flex min-h-0 flex-1 flex-col', step !== 'folder' && 'hidden')}>
              <Label className="shrink-0">
                {selectedProvider === 'outlook'
                  ? 'Select Outlook folders to sync'
                  : 'Select Source Folder to Sync'}
              </Label>
              <div className="mt-2 min-h-0 flex-1 overflow-hidden rounded-md border">
                {isMicrosoftFilesProvider ? (
                  <MicrosoftFilesPicker
                    providerType={selectedProvider === 'sharepoint' ? 'sharepoint' : 'onedrive'}
                    accessToken={accessToken}
                    onSelect={handleOutlookFolderSelected}
                    selectedFolderId={selectedFolder?.id}
                  />
                ) : (
                  <WorkspacePicker
                    providerType={selectedProvider}
                    providerConfig={providerConfig}
                    accessToken={accessToken}
                    matterGuid={matterGuid}
                    onSelect={handleOutlookFolderSelected}
                    selectedFolderId={selectedFolder?.id}
                    multiSelect={selectedProvider === 'outlook'}
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 4: Destination Folder Selection */}
          {step === 'destination' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0">
                <Label>Select Destination Folder in Crimson</Label>
                <p className="mt-2 text-sm text-gray-500">
                  {isMicrosoftFilesProvider
                    ? `Choose where ${selectedProvider === 'sharepoint' ? 'SharePoint' : 'OneDrive'} files will be stored in this matter`
                    : 'Choose where synced documents will be stored in this matter'}
                </p>
              </div>

              {loadingFolders ? (
                <div className="flex h-32 items-center justify-center">
                  <Loading text='Loading folders' />
                </div>
              ) : (
                <div className="mt-2 min-h-0 flex-1 overflow-y-auto rounded-md border">
                  <FolderTree
                    folders={folderTree}
                    totalDocuments={0}
                    matterSlug={matterGuid}
                    onRefresh={() => {}}
                    navigationMode="callback"
                    onFolderSelect={(folder) => setDestinationFolder(folder)}
                    selectedFolderGuid={destinationFolder?.guid || null}
                    showFolderActions={false}
                    hideAllFiles={isMicrosoftFilesProvider || selectedProvider === 'outlook'}
                    allowExpandWhenDisabled={true}
                    shouldDisableFolder={
                      selectedProvider === 'outlook'
                        ? (folder) => {
                            if (folder.name.toLowerCase() === 'attachments') {
                              return true
                            }
                            return !isInCorrespondenceFolderTree(folder)
                          }
                          : undefined
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex justify-between w-full">
            <div>
              {step !== 'provider' && step !== 'auth' && (
                <NewButton
                  variant="outline"
                  onClick={() => {
                    if (step === 'destination') {
                      // Clear destination selection when going back
                      setDestinationFolder(undefined)
                      setStep('folder')
                    } else if (step === 'folder') {
                      // Since we auto-trigger auth for iManage, always go back to provider
                      // Clear folder selection and auth state
                      setSelectedFolder(null)
                      setAccessToken(null)
                      setProviderConfig({})
                      setStep('provider')
                    }
                  }}
                  text='Back'
                />
              )}
            </div>

            <div className="flex gap-2">
              <NewButton variant="outline" onClick={() => setIsOpen(false)} text='Cancel' />

              {step === 'folder' && (
                <NewButton
                  onClick={handleFolderNext}
                  disabled={selectedProvider === 'outlook' ? selectedFolders.length === 0 : !selectedFolder}
                  text='Next'
                />
              )}

              {step === 'destination' && (
                <NewButton
                  onClick={handleCreateSync}
                  disabled={destinationFolder === undefined || isCreating}
                  loading={isCreating}
                  loadingText='Creating Sync'
                  text='Create Sync'
                />
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      </>
      )}
    </Dialog>
  )
}
